import React, { useState, useCallback } from 'react';
import 'antd/dist/reset.css';
import {
  Layout, Button, Modal, Input, message, Tabs, Upload,
  Space, Card, Typography, Row, Col, Form, Select, Spin, Radio
} from 'antd';
import {
  PlusOutlined, UploadOutlined, SmileOutlined,
  FrownOutlined, MehOutlined, DownloadOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { SignedIn, SignedOut, SignInButton, UserButton } from '@clerk/clerk-react';

const SignInPage = () => {
  return (
    <Layout style={{
      minHeight: '100vh',
      background: '#f5f5f5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Card
        style={{
          width: 320,
          textAlign: 'center',
          borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}
        bodyStyle={{ padding: '32px' }}
      >
        <Typography.Title
          level={3}
          style={{
            color: '#1677ff',
            marginBottom: 24
          }}
        >
          SEO AI Agent
        </Typography.Title>

        <SignInButton mode="modal">
          <Button
            type="primary"
            size="large"
            block
            style={{ height: 40 }}
          >
            登录
          </Button>
        </SignInButton>
      </Card>
    </Layout>
  );
};

const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_BASE_URL;
  const currentOrigin = window.location.origin;

  return envUrl || currentOrigin;
};

const API_BASE_URL = getApiBaseUrl();

function App() {
  const [projects, setProjects] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [activeProjectIndex, setActiveProjectIndex] = useState(null);
  const [projectStates, setProjectStates] = useState({});
  const [projectLogs, setProjectLogs] = useState({});

  const [projectPromptFields, setProjectPromptFields] = useState({});

  const [customPromptModalOpen, setCustomPromptModalOpen] = useState(false);
  const [customPromptEditing, setCustomPromptEditing] = useState('');

  const [informationalCustomPromptModalOpen, setInformationalCustomPromptModalOpen] = useState(false);
  const [informationalCustomPromptEditing, setInformationalCustomPromptEditing] = useState('');

  const [modelSelectModalOpen, setModelSelectModalOpen] = useState(false);

  const [wpConfigModalOpen, setWpConfigModalOpen] = useState(false);
  const [projectWpConfig, setProjectWpConfig] = useState({});
  const [wpConfigEditing, setWpConfigEditing] = useState({ wp_url: '', wp_username: '', wp_app_password: '' });
  const [shopifyConfigModalOpen, setShopifyConfigModalOpen] = useState(false);

  const [wpTestStatus, setWpTestStatus] = useState(null);
  const [wpTesting, setWpTesting] = useState(false);

  const [projectShopifyConfig, setProjectShopifyConfig] = useState({});
  const [shopifyConfigEditing, setShopifyConfigEditing] = useState({ shop_domain: '', access_token: '' });
  const [shopifyTesting, setShopifyTesting] = useState(false);
  const [shopifyTestStatus, setShopifyTestStatus] = useState(null);

  const handleOpenWpConfig = () => {
    const cfg = projectWpConfig[activeProjectName] || { wp_url: '', wp_username: '', wp_app_password: '' };
    setWpConfigEditing(cfg);
    setWpConfigModalOpen(true);
    setWpTestStatus(null);
  };
  const handleOpenShopifyConfig = () => {
    const cfg = projectShopifyConfig[activeProjectName] || { shop_domain: '', access_token: '' };
    setShopifyConfigEditing(cfg);
    setShopifyTestStatus(null);
    setShopifyConfigModalOpen(true);
  };

  const handleTestWpConnection = async () => {
    setWpTesting(true);
    setWpTestStatus(null);
    try {
      const { wp_url, wp_username, wp_app_password } = wpConfigEditing;
      const res = await axios.post(`${API_BASE_URL}/api/test_wp_connection`, {
        wp_url,
        wp_username,
        wp_app_password,
      });
      if (res.data.type === 'success') {
        setWpTestStatus('success');
        message.success('连接成功！');
      } else {
        setWpTestStatus('fail');
        message.error(res.data.message || '连接失败');
      }
    } catch (e) {
      setWpTestStatus('fail');
      message.error('连接失败，请检查填写信息和网络');
    } finally {
      setWpTesting(false);
    }
  };

  const handleTestShopifyConnection = async () => {
    setShopifyTesting(true);
    setShopifyTestStatus(null);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/test_shopify_connection`, {
        shop_domain: shopifyConfigEditing.shop_domain,
        access_token: shopifyConfigEditing.access_token,
      });
      if (res.data && res.data.type === 'success') {
        setShopifyTestStatus('success');
        message.success('连接成功！')
      } else {
        setShopifyTestStatus('fail');
        message.error(res.data.message || '连接失败');
      }
    } catch {
      setShopifyTestStatus('fail');
      message.error('连接失败，请检查填写信息和网络');
    } finally {
      setShopifyTesting(false);
    }
  };

  const modelList = [
    { group: 'ChatGPT', value: 'gpt-4o', label: 'ChatGPT 4o' },
    { group: 'ChatGPT', value: 'gpt-4.1', label: 'ChatGPT 4.1' },
    { group: 'ChatGPT', value: 'gpt-4o-mini', label: 'ChatGPT 4o-mini' },
    { group: 'Claude', value: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4 (20250514)' },
    { group: 'Claude', value: 'claude-3-7-sonnet-latest', label: 'Claude 3.7 Sonnet Latest' },
    { group: 'Claude', value: 'claude-3-5-haiku-latest', label: 'Claude 3.5 Haiku Latest' },
    { group: 'Claude', value: 'claude-3-5-sonnet-latest', label: 'Claude 3.5 Sonnet Latest' },
  ];

  const [globalModel, setGlobalModel] = useState('claude-3-7-sonnet-latest');
  const [modelSelectTemp, setModelSelectTemp] = useState(globalModel);

  const defaultProjectState = {
    uploading: false,
    downloadFileName: null,
    canDownloadMarkdown: false,
    canDownloadJson: false,
    downloadMarkdownUrl: '',
    downloadJsonUrl: '',
    analyzedKeywords: [],
    selectedKeyword: null,
    selectedKeywordData: null,
    loadingKeywords: false,
    blogExcelUrl: '',
    blogGenerating: false,
    informationalKeywords: [],
    informationalSelectedKeyword: null,
    informationalSelectedKeywordData: null,
    informationalLoadingKeywords: false,
    informationalBlogExcelUrl: '',
    informationalBlogGenerating: false,
    informationalCanDownloadMarkdown: false,
    informationalCanDownloadJson: false,
    informationalDownloadMarkdownUrl: '',
    informationalDownloadJsonUrl: '',
    generatedInformationalArticles: [],
    shopifyBlogs: [],
    loadingShopifyBlogs: false,
    selectedShopifyBlogId: null,
    customPrompt: '',
    informationalCustomPrompt: '',
  };

  const defaultPromptFields = {
    language: '',
    tone: '',
    persona: '',
    audience: '',
    word_count: '',
    h2_count: '',
    section_word_count: '',
    main_section_word_count: '',
    seo_title: '',
    meta_description: '',
    keywords: '',
    brand_voice: '',
    product_info: '',
    // Informational相关字段
    brand_name: '',
    product_or_parts: '',
    target_market: '',
    company_advantages: '',
  };
  const activeProjectName = projects[activeProjectIndex] || '未选择项目';
  const promptFields = projectPromptFields[activeProjectName] || defaultPromptFields;
  const logs = projectLogs[activeProjectName] || [];
  const activeState = projectStates[activeProjectName] || defaultProjectState;

  const showModal = () => setIsModalOpen(true);
  const handleCancel = () => {
    setIsModalOpen(false);
    setNewProjectName('');
  };

  const handleOk = () => {
    if (!newProjectName.trim()) {
      message.error('项目名称不能为空');
      return;
    }
    const newName = newProjectName.trim();
    if (projects.includes(newName)) {
      message.error('项目名称不能重复');
      return;
    }
    setProjects([...projects, newName]);
    setProjectStates(prev => ({
      ...prev,
      [newName]: { ...defaultProjectState }
    }));
    setActiveProjectIndex(projects.length); // 自动选中
    setIsModalOpen(false);
    setNewProjectName('');
  };

  const handlePromptFieldChange = (field, value) => {
    setProjectPromptFields(prev => ({
      ...prev,
      [activeProjectName]: {
        ...prev[activeProjectName],
        [field]: value
      }
    }));
  };

  const addLog = (type, message) => {
    if (!activeProjectName) return;
    const timestamp = new Date().toLocaleTimeString();
    setProjectLogs(prev => ({
      ...prev,
      [activeProjectName]: [
        ...(prev[activeProjectName] || []),
        { type, message, timestamp }
      ]
    }));
  };

  const updateProjectState = (projectName, newState) => {
    setProjectStates(prev => ({
      ...prev,
      [projectName]: {
        ...prev[projectName],
        ...newState
      }
    }));
  };

  const handleExcelUpload = async (info) => {
    const file = info?.fileList?.[0]?.originFileObj;
    if (!file || !activeProjectName) return;
    addLog('success', `正在处理中，请稍后，目前已选择文件：${file.name}`);
    updateProjectState(activeProjectName, { uploading: true });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('name_prompt', 'output');
    formData.append('project', activeProjectName);

    const uploadUrl = `${API_BASE_URL}/api/upload_excel`;

    try {
      const response = await axios.post(
        uploadUrl,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 1800000  // 30分钟超时
        }
      );
      addLog(response.data.type || 'success', response.data.message || '上传成功');
      if (response.data.file_name) {
        updateProjectState(activeProjectName, {
          downloadFileName: response.data.file_name,
        });
      }
    } catch (error) {
      addLog('error', '文件上传失败，请检查后端服务是否启动');
    } finally {
      updateProjectState(activeProjectName, { uploading: false });
    }
  };

  const fetchAnalyzedKeywords = async (projectName, jsonFile) => {
    updateProjectState(projectName, { loadingKeywords: true });
    try {
      const res = await axios.get(`${API_BASE_URL}/api/download_file?file_name=${jsonFile}`);
      let data = res.data;
      if (typeof data === 'string') {
        data = JSON.parse(data);
      }
      updateProjectState(projectName, {
        analyzedKeywords: data,
        selectedKeyword: null,
        selectedKeywordData: null,
      });
    } catch (e) {
      message.error('获取分析结果失败');
      updateProjectState(projectName, { analyzedKeywords: [] });
    } finally {
      updateProjectState(projectName, { loadingKeywords: false });
    }
  };

  const fetchInformationalKeywords = async (projectName, jsonFile) => {
    updateProjectState(projectName, { informationalLoadingKeywords: true });
    try {
      const res = await axios.get(`${API_BASE_URL}/api/download_file?file_name=${jsonFile}`);
      let data = res.data;
      if (typeof data === 'string') {
        data = JSON.parse(data);
      }
      updateProjectState(projectName, {
        informationalKeywords: data,
        informationalSelectedKeyword: null,
        informationalSelectedKeywordData: null,
      });
    } catch (e) {
      message.error('获取Informational分析结果失败');
      updateProjectState(projectName, { informationalKeywords: [] });
    } finally {
      updateProjectState(projectName, { informationalLoadingKeywords: false });
    }
  };

  const handleBlogExcelUpload = async (info) => {
    const file = info?.fileList?.[0]?.originFileObj;
    if (!file || !activeProjectName) return;
    addLog('success', `正在处理中，请稍后，目前已选择文件：${file.name}`);
    updateProjectState(activeProjectName, { uploading: true });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('project', activeProjectName);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/upload_blog_excel`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 1800000  // 30分钟超时
        }
      );
      addLog(response.data.type || 'success', response.data.message || '上传成功');
      if (response.data.keywords) {
        updateProjectState(activeProjectName, {
          analyzedKeywords: response.data.keywords.map(k => ({ keyword: k })),
        });
      }
      if (response.data.markdown_file && response.data.json_file) {
        updateProjectState(activeProjectName, {
          downloadMarkdownUrl: `${API_BASE_URL}/api/download_file?file_name=${response.data.markdown_file}`,
          downloadJsonUrl: `${API_BASE_URL}/api/download_file?file_name=${response.data.json_file}`,
          canDownloadMarkdown: true,
          canDownloadJson: true,
        });
        fetchAnalyzedKeywords(activeProjectName, response.data.json_file);
      } else {
        updateProjectState(activeProjectName, {
          canDownloadMarkdown: false,
          canDownloadJson: false,
          analyzedKeywords: [],
        });
      }
    } catch (error) {
      addLog('error', '文件上传失败，请检查后端服务是否启动');
      updateProjectState(activeProjectName, {
        canDownloadMarkdown: false,
        canDownloadJson: false,
        analyzedKeywords: [],
      });
    } finally {
      updateProjectState(activeProjectName, { uploading: false });
    }
  };

  const handleInformationalExcelUpload = async (info) => {
    const file = info?.fileList?.[0]?.originFileObj;
    if (!file || !activeProjectName) return;
    addLog('success', `正在处理Informational文件，请稍后，目前已选择文件：${file.name}`);
    updateProjectState(activeProjectName, { uploading: true });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('project', activeProjectName);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/upload_informational_excel`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 1800000  // 30分钟超时
        }
      );
      addLog(response.data.type || 'success', response.data.message || 'Informational文件上传成功');
      if (response.data.keywords) {
        updateProjectState(activeProjectName, {
          informationalKeywords: response.data.keywords.map(k => ({ keyword: k })),
        });
      }
      if (response.data.markdown_file && response.data.json_file) {
        updateProjectState(activeProjectName, {
          informationalDownloadMarkdownUrl: `${API_BASE_URL}/api/download_file?file_name=${response.data.markdown_file}`,
          informationalDownloadJsonUrl: `${API_BASE_URL}/api/download_file?file_name=${response.data.json_file}`,
          informationalCanDownloadMarkdown: true,
          informationalCanDownloadJson: true,
        });
        fetchInformationalKeywords(activeProjectName, response.data.json_file);
      } else {
        updateProjectState(activeProjectName, {
          informationalCanDownloadMarkdown: false,
          informationalCanDownloadJson: false,
          informationalKeywords: [],
        });
      }
    } catch (error) {
      addLog('error', 'Informational文件上传失败，请检查后端服务是否启动');
      updateProjectState(activeProjectName, {
        informationalCanDownloadMarkdown: false,
        informationalCanDownloadJson: false,
        informationalKeywords: [],
      });
    } finally {
      updateProjectState(activeProjectName, { uploading: false });
    }
  };

  const handleSelectKeyword = (value) => {
    const found = activeState.analyzedKeywords.find(item => item.keyword === value);
    updateProjectState(activeProjectName, {
      selectedKeyword: value,
      selectedKeywordData: found || null
    });
  };

  const handleSelectInformationalKeyword = (value) => {
    const found = activeState.informationalKeywords.find(item => item.keyword === value);
    updateProjectState(activeProjectName, {
      informationalSelectedKeyword: value,
      informationalSelectedKeywordData: found || null
    });
  };

  const handleGenerateBlogs = async () => {
    // 每次点击都重新获取最新的 state
    const currentActiveState = projectStates[activeProjectName] || defaultProjectState;
    const customPrompt = currentActiveState.customPrompt;
    const analyzedKeywords = currentActiveState.analyzedKeywords;

    if (!analyzedKeywords.length) {
      message.warning('请先上传并分析关键词');
      return;
    }
    updateProjectState(activeProjectName, { blogGenerating: true });
    try {
      const blog_inputs = analyzedKeywords.map(item => ({
        keyword: item.keyword,
        prompt_inputs: customPrompt
          ? { custom_prompt: customPrompt, keyword: item.keyword } // 仅传自定义
          : {
              language: promptFields.language,
              tone: promptFields.tone,
              persona: promptFields.persona,
              audience: promptFields.audience,
              word_count: promptFields.word_count,
              h2_count: promptFields.h2_count,
              keyword: item.keyword,
              serp_json: JSON.stringify(item.serp_data, null, 2),
            },
      }));
  
      const res = await axios.post(`${API_BASE_URL}/api/generate_blogs`, { blog_inputs, project: activeProjectName, model: globalModel });
      if (res.data && res.data.excel_file) {
        updateProjectState(activeProjectName, {
          blogExcelUrl: `${API_BASE_URL}/api/download_file?file_name=${res.data.excel_file}`
        });
        message.success('Blog生成成功，可下载Excel');
        addLog('success', `所有关键词的 Blog 已生成完毕，请导出查看'`);
        if (res.data.logs && Array.isArray(res.data.logs)) {
          res.data.logs.forEach(logMsg => addLog('success', logMsg));
        }
      } else {
        updateProjectState(activeProjectName, { blogExcelUrl: '' });
        message.error('Blog生成失败');
      }
    } catch (e) {
      updateProjectState(activeProjectName, { blogExcelUrl: '' });
      message.error('Blog生成失败');
    } finally {
      updateProjectState(activeProjectName, { blogGenerating: false });
    }
  };

  const handleGenerateInformationalBlogs = async () => {
    // 每次点击都重新获取最新的 state
    const currentActiveState = projectStates[activeProjectName] || defaultProjectState;
    const informationalCustomPrompt = currentActiveState.informationalCustomPrompt;
    const informationalKeywords = currentActiveState.informationalKeywords;

    if (!informationalKeywords.length) {
      message.warning('请先上传并分析Informational关键词');
      return;
    }
    updateProjectState(activeProjectName, { informationalBlogGenerating: true });
    try {
      // 为每个informational关键词组装独立的、包含所有必需字段的 prompt_inputs
      const blog_inputs = informationalKeywords.map(item => ({
        keyword: item.keyword,
        prompt_inputs: informationalCustomPrompt
          ? {
              custom_prompt: informationalCustomPrompt,
              keyword: item.keyword,
            }
          : {
              brand_name: promptFields.brand_name || 'Our Brand',
              product_or_parts: promptFields.product_or_parts || 'products',
              target_market: promptFields.target_market || 'general customers',
              language: promptFields.language || 'English',
              company_advantages: promptFields.company_advantages || 'quality and reliability',
              keyword: item.keyword,
              // 将完整的SERP数据作为JSON字符串传入
              serp_json: JSON.stringify(item.serp_data, null, 2),
            },
      }));

      const res = await axios.post(`${API_BASE_URL}/api/generate_informational_blogs`, { blog_inputs, project: activeProjectName });
      if (res.data && res.data.excel_file) {
        // 生成文章列表用于上传
        const articles = blog_inputs.map(item => ({
          keyword: item.keyword,
          article_id: `informational_blog_${activeProjectName.replace(/[^a-zA-Z0-9_]/g, '')}_${item.keyword.replace(/[^a-zA-Z0-9_]/g, '')}.json`
        }));

        updateProjectState(activeProjectName, {
          informationalBlogExcelUrl: `${API_BASE_URL}/api/download_file?file_name=${res.data.excel_file}`,
          generatedInformationalArticles: articles
        });
        message.success('Informational Blog生成成功，可下载Excel');
        addLog('success', `所有Informational关键词的 Blog 已生成完毕，可上传到WordPress或Shopify`);
        if (res.data.logs && Array.isArray(res.data.logs)) {
          res.data.logs.forEach(logMsg => addLog('success', logMsg));
        }
      } else {
        updateProjectState(activeProjectName, { informationalBlogExcelUrl: '' });
        message.error('Informational Blog生成失败');
      }
    } catch (e) {
      updateProjectState(activeProjectName, { informationalBlogExcelUrl: '' });
      message.error('Informational Blog生成失败');
    } finally {
      updateProjectState(activeProjectName, { informationalBlogGenerating: false });
    }
  };

  const handleUploadToWordPress = async (articleId) => {
    const cfg = projectWpConfig[activeProjectName] || {};
    try {
      const res = await axios.post(`${API_BASE_URL}/api/upload_to_wordpress`, {
        article_id: articleId,
        wp_url: cfg.wp_url,
        wp_username: cfg.wp_username,
        wp_app_password: cfg.wp_app_password,
      });
      message.success(`WordPress上传成功: ${res.data.message}`);
      addLog('success', `文章 ${articleId} 已上传到WordPress`);
    } catch (e) {
      message.error(`WordPress上传失败: ${e.response?.data?.detail || e.message}`);
      addLog('error', `WordPress上传失败: ${e.response?.data?.detail || e.message}`);
    }
  };

  const fetchShopifyBlogs = async () => {
    updateProjectState(activeProjectName, { loadingShopifyBlogs: true });
    try {
      const cfg = projectShopifyConfig[activeProjectName] || {};
      if (!cfg.shop_domain || !cfg.access_token) {
        message.error('请先配置Shopify连接信息');
        addLog('error', '请先配置Shopify连接信息');
        return;
      }

      const res = await axios.post(`${API_BASE_URL}/api/get_shopify_blogs`, {
        shop_domain: cfg.shop_domain,
        access_token: cfg.access_token,
      });
      if (res.data.type === 'success') {
        updateProjectState(activeProjectName, { shopifyBlogs: res.data.blogs });
        addLog('success', `成功获取到 ${res.data.count} 个Shopify blogs`);
      } else {
        message.error('获取Shopify blogs失败');
        addLog('error', '获取Shopify blogs失败');
      }
    } catch (e) {
      message.error(`获取Shopify blogs失败: ${e.response?.data?.detail || e.message}`);
      addLog('error', `获取Shopify blogs失败: ${e.response?.data?.detail || e.message}`);
    } finally {
      updateProjectState(activeProjectName, { loadingShopifyBlogs: false });
    }
  };

  const handleSelectShopifyBlog = (blogId) => {
    updateProjectState(activeProjectName, { selectedShopifyBlogId: blogId });
  };

  const handleUploadToShopify = async (articleId, blogId) => {
    const cfg = projectShopifyConfig[activeProjectName] || {};
    try {
      const res = await axios.post(`${API_BASE_URL}/api/upload_to_shopify`, {
        article_id: articleId,
        blog_id: blogId,
        shop_domain: cfg.shop_domain,
        access_token: cfg.access_token,
      });
      message.success(`Shopify上传成功: ${res.data.message}`);
      addLog('success', `文章 ${articleId} 已上传到Shopify`);
    } catch (e) {
      message.error(`Shopify上传失败: ${e.response?.data?.detail || e.message}`);
      addLog('error', `Shopify上传失败: ${e.response?.data?.detail || e.message}`);
    }
  };

  const renderPromptPreview = useCallback((selectedKeywordData) => {
    let keywordBlock = '';
    // 只有当选择了关键词并且该关键词有SERP数据时，才附加分析结果
    if (selectedKeywordData && selectedKeywordData.serp_data) {
      const organicResults =
        selectedKeywordData.serp_data.organic_results && selectedKeywordData.serp_data.organic_results.length > 0
          ? selectedKeywordData.serp_data.organic_results.map(r => `- ${r.title} (${r.link})`).join('\n')
          : '无';
      const relatedSearches =
        selectedKeywordData.serp_data.related_searches && selectedKeywordData.serp_data.related_searches.length > 0
          ? selectedKeywordData.serp_data.related_searches.map(s => `- ${s}`).join('\n')
          : '无';
      keywordBlock = `\n\n---
\n**附加关键词分析数据**
\n**当前关键词:** ${selectedKeywordData.keyword}
\n**相关结果:**
${organicResults}
\n**相关搜索:**
${relatedSearches}
`;
    }

    // 返回基础模板 + 动态字段 + 关键词分析数据
    return `Guidelines

1. Language
   - Write in ${promptFields.language || '__________'}.
   - Maintain a ${promptFields.tone || '__________'} tone throughout.
   - Persona: ${promptFields.persona || '__________'}

2. SEO Structure
   - Create an SEO-friendly title that includes the primary keyword.
   - Write a concise meta description (120–160 characters) that incorporates the main keyword.

3. Audience
   - Target people ${promptFields.audience || '__________'}.

4. Content Organization
   - Total length: ${promptFields.word_count || '__________'}.
   - Structure the post with ${promptFields.h2_count || '__________'}.
     - Each H2 heading should be short, concise, and concrete (avoid vague terms like "Understanding" or "Importance").
     - Under each H2, write a detailed section of 200–300 words that expands on the heading with in-depth information (not just a brief overview).
     - The core section of the article (main topic) should be 300–500 words.
     - Less crucial sections can be shorter (around 150–200 words).
   - Use bold text to highlight key points, and include block quotes or cited facts where appropriate.
   - Integrate a table or other rich content elements to improve readability and depth.

5. Keyword Usage
   - Distribute the primary keyword, related terms, and LSI keywords naturally and evenly throughout the article.

6. References and Authority
   - Where relevant, cite authoritative sources or statistics from reputable websites to support claims.
   - Insert internal links based on the uploaded site links (products, categories, pages, and other blog posts).
     - Include only 1–3 links within the first 1–3 paragraphs.
     - Keep the total number of internal links within 5.

7. Brand Voice & Guidelines
   - Reflect the brand's tone as described in the provided reference document.
   - Avoid overt marketing or sales language; focus on value and expertise.

---

Reference

Please refer to the attached document for the topic background information, brand positioning, product information, and tone of voice.  
Exclude all irrelevant brands in the file except the one you are writing for.
${keywordBlock}`; // 在末尾拼接关键词分析模块
  }, [promptFields]);

  const renderInformationalPromptPreview = useCallback((selectedKeywordData) => {
    let keywordBlock = '';
    // 只有当选择了关键词并且该关键词有SERP数据时，才附加分析结果
    if (selectedKeywordData && selectedKeywordData.serp_data) {
      const organicResults =
        selectedKeywordData.serp_data.organic_results && selectedKeywordData.serp_data.organic_results.length > 0
          ? selectedKeywordData.serp_data.organic_results.map(r => `- ${r.title} (${r.link})`).join('\n')
          : '无';
      const relatedSearches =
        selectedKeywordData.serp_data.related_searches && selectedKeywordData.serp_data.related_searches.length > 0
          ? selectedKeywordData.serp_data.related_searches.map(s => `- ${s}`).join('\n')
          : '无';
      keywordBlock = `\n\n---
\n**附加关键词分析数据**
\n**当前关键词:** ${selectedKeywordData.keyword}
\n**相关结果:**
${organicResults}
\n**相关搜索:**
${relatedSearches}
`;
    }

    // 返回informational模板 + 动态字段 + 关键词分析数据
    return `Role:
Write on behalf of ${promptFields.brand_name || '__________'}.

Topic:
Create an SEO friendly and authoritative and informative product category page content, use the attached file of the company and product introduction.

Requirements
SEO Title
- Write an SEO-friendly title (max 60 characters), ending with: | ${promptFields.brand_name || '__________'}.
Meta Description
- Compose a concise meta description (max 150 characters) that summarizes the product category and typical use scenarios.
Audience Analysis
- Target potential customers seeking information about or looking to purchase ${promptFields.product_or_parts || '__________'}.
Target Market
- ${promptFields.target_market || '__________'}
Writing Style & Language
- Style: Professional, using the first-person perspective.
- Language: ${promptFields.language || 'English'}; clear and accessible, avoiding overly academic or technical jargon.
- Content Focus: Provide detailed, specific information, including relevant technical details where appropriate.
Company & Product Advantages
- ${promptFields.company_advantages || '__________'}

Content Structure
1. Reference Style:
   - Mimic the style of the provided reference copy, using first-person voice, but do not mention or use any brand names from the reference.
2. Headings:
   - Organize the page with multiple H2 headings. For each H2, write 1–2 concise paragraphs (100–200 words per section).
3. Keyword Integration:
   - Evenly and naturally distribute the core keywords, keyword variations, LSI (Latent Semantic Indexing) terms, and other relevant keywords throughout the content.
4. Rich Content Elements:
   - Where relevant, add a comparison table or list that clearly shows different product types and their main features/specs for easy comparison.
5. Technical Details:
   - Include key technical specifications or distinguishing features as needed, to help users make informed decisions.

Reference:
- Please refer to the attached document for the topic background information, brand positioning, product information, and tone of voice. Please exclude all the irrelevant brands in the file except for the brand you are writing for.
- Use the following SERP analysis data to inform your writing. This includes top competing pages, common questions, and related searches for the keyword "${selectedKeywordData?.keyword || '__________'}".
${keywordBlock}`; // 在末尾拼接关键词分析模块
  }, [promptFields]);

  return (
    <>
      <SignedOut>
        <SignInPage />
      </SignedOut>

      <SignedIn>
        <Layout style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          {/* 顶部认证栏 */}
          <Layout.Header style={{
            background: '#fff',
            padding: '0 24px',
            borderBottom: '1px solid #f0f0f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '64px'
          }}>
            <Typography.Title level={3} style={{ margin: 0, color: '#1677ff' }}>
              SEO AI Agent
            </Typography.Title>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Typography.Text style={{ color: '#666', fontSize: 14 }}>
                欢迎回来！
              </Typography.Text>
              <UserButton
                appearance={{
                  elements: {
                    avatarBox: {
                      width: 36,
                      height: 36
                    }
                  }
                }}
              />
            </div>
          </Layout.Header>

          {/* 主体布局 */}
          <Layout style={{ flex: 1, display: 'flex', flexDirection: 'row' }}>
            {/* 左侧栏 */}
            <Layout.Sider
              width={240}
              style={{
                background: '#fff',
                padding: '16px 12px',
                borderRight: '1px solid #f0f0f0',
                position: 'relative',
                height: 'calc(100vh - 64px)',  // 减去header高度
              }}
            >
              {/* 顶部：项目列表 */}
              <div>
                <div style={{ textAlign: 'center', paddingTop: 4 }}>
                  <Typography.Title level={5} style={{ marginBottom: 12 }}>
                    项目列表
                  </Typography.Title>
                  {projects.length === 0 && (
                    <div style={{ color: '#aaa', marginBottom: 16 }}>暂无项目</div>
                  )}
                  {projects.map((item, idx) => (
                    <Card
                      key={idx}
                      size="small"
                      hoverable
                      onClick={() => setActiveProjectIndex(idx)}
                      style={{
                        marginBottom: 16,
                        borderRadius: 12,
                        border: activeProjectIndex === idx ? '2px solid #1677ff' : '1px solid #000',
                        boxShadow: activeProjectIndex === idx
                          ? '0 0 6px rgba(22, 119, 255, 0.6)'
                          : '0 2px 8px rgba(0,0,0,0.08)',
                        textAlign: 'center',
                        cursor: 'pointer',
                        backgroundColor: activeProjectIndex === idx ? '#e6f4ff' : '#fff',
                      }}
                      bodyStyle={{ padding: 16 }}
                    >
                      {item}
                    </Card>
                  ))}
                  <Button
                    type="primary"
                    shape="circle"
                    icon={<PlusOutlined />}
                    onClick={showModal}
                    disabled={projects.length >= 10}
                    style={{ marginTop: 24 }}
                  />
                </div>
                {/* 新增项目弹窗 */}
                <Modal
                  title="新增项目"
                  open={isModalOpen}
                  onOk={handleOk}
                  onCancel={handleCancel}
                  okText="确定"
                  cancelText="取消"
                >
                  <Input
                    placeholder="请输入项目名称"
                    value={newProjectName}
                    onChange={e => setNewProjectName(e.target.value)}
                    maxLength={20}
                  />
                </Modal>
              </div>

              {/* 底部：模型选择按钮 */}
              <Button
                block
                type="default"
                style={{
                  position: 'absolute', bottom: 32, left: 12, width: 'calc(100% - 24px)', fontSize: '12.5px',
                }}
                onClick={() => {
                  setModelSelectTemp(globalModel);
                  setModelSelectModalOpen(true);
                }}
              >
                当前模型：{modelList.find(m => m.value === globalModel)?.label || globalModel}
              </Button>
            </Layout.Sider>


            {/* 右侧内容区域 */}
            <Layout style={{ flex: 1, background: '#fff', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
              <Layout.Content
                style={{
                  flex: '1 1 auto',
                  overflowY: 'auto',
                  padding: '24px 32px',
                }}
              >
                {projects.length === 0 || activeProjectIndex === null ? (
                  <div style={{ color: '#999', fontSize: 16 }}>请先添加并选择一个项目</div>
                ) : (
                  <>
                    <div style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 16 }}>
                      当前项目：{activeProjectName}
                    </div>
                    <Space>
                      <Button
                        type="primary"
                        size="small"
                        onClick={handleOpenWpConfig}
                        style={{ marginRight: 8 }}
                      >
                        配置WordPress
                      </Button>
                      <Button
                        type="primary"
                        size="small"
                        onClick={handleOpenShopifyConfig}
                      >
                        配置Shopify
                      </Button>
                    </Space>

                    <Tabs
                      defaultActiveKey="1"
                      items={[
                        {
                          label: '关键词分类',
                          key: '1',
                          children: (
                            <div>
                              <Space size={28}>
                                <Upload
                                  beforeUpload={() => false}
                                  accept=".xls,.xlsx"
                                  maxCount={1}
                                  showUploadList={false}
                                  onChange={handleExcelUpload}
                                >
                                  <Button
                                    type="primary"
                                    icon={<UploadOutlined />}
                                    loading={activeState.uploading}
                                  >
                                    上传文件
                                  </Button>
                                </Upload>
                                <Button
                                  type="primary"
                                  icon={<DownloadOutlined />}
                                  disabled={!activeState.downloadFileName}
                                  onClick={() => {
                                    if (activeState.downloadFileName) {
                                      window.open(
                                        `${API_BASE_URL}/api/download_excel?file_name=${activeState.downloadFileName}`
                                      );
                                    }
                                  }}
                                >
                                  下载文件
                                </Button>
                              </Space>
                            </div>
                          ),
                        },
                        {
                          label: 'SEO 文案生成',
                          key: '2',
                          children: (
                            <div>
                              <Space size={28}>
                                <Upload
                                  beforeUpload={() => false}
                                  accept=".xls,.xlsx"
                                  maxCount={1}
                                  showUploadList={false}
                                  onChange={handleBlogExcelUpload}
                                >
                                  <Button type="primary" icon={<UploadOutlined />} loading={activeState.uploading}>
                                    Excel上传
                                  </Button>
                                </Upload>
                                <Button
                                  type="primary"
                                  icon={<DownloadOutlined />}
                                  disabled={!activeState.canDownloadMarkdown}
                                  onClick={() => {
                                    if (activeState.canDownloadMarkdown && activeState.downloadMarkdownUrl) {
                                      window.open(activeState.downloadMarkdownUrl);
                                    } else {
                                      message.warning('暂无可下载的Markdown文件');
                                    }
                                  }}
                                >下载SERP结果MD</Button>
                                <Button
                                  type="primary"
                                  icon={<DownloadOutlined />}
                                  disabled={!activeState.canDownloadJson}
                                  onClick={() => {
                                    if (activeState.canDownloadJson && activeState.downloadJsonUrl) {
                                      window.open(activeState.downloadJsonUrl);
                                    } else {
                                      message.warning('暂无可下载的JSON文件');
                                    }
                                  }}
                                >下载SERP结果JSON</Button>
                                <Button
                                  type="primary"
                                  icon={<PlusOutlined />}
                                  loading={activeState.blogGenerating}
                                  onClick={handleGenerateBlogs}
                                >SEO文案生成</Button>
                                <Button
                                  type="primary"
                                  icon={<DownloadOutlined />}
                                  disabled={!activeState.blogExcelUrl}
                                  onClick={() => {
                                    if (activeState.blogExcelUrl) {
                                      window.open(activeState.blogExcelUrl);
                                    } else {
                                      message.warning('暂无可下载的Blog Excel文件');
                                    }
                                  }}
                                >
                                  SEO文案导出
                                </Button>
                                <Button
                                  type="primary"
                                  onClick={() => {
                                    setCustomPromptEditing(activeState.customPrompt || '');
                                    setCustomPromptModalOpen(true);
                                  }}
                                >
                                  Prompt自定义
                                </Button>
                              </Space>
                              <div style={{ marginTop: 32, border: '1px solid #ccc', borderRadius: 6, padding: 24 }}>
                                <Row gutter={32}>
                                  <Col span={10}>
                                    <div style={{ fontWeight: 'bold', marginBottom: 12 }}>Prompt 修改区</div>
                                    <Form layout="vertical">
                                      <Form.Item label="语言：">
                                        <Input
                                          value={promptFields.language}
                                          placeholder="请输入语言"
                                          onChange={e => handlePromptFieldChange('language', e.target.value)}
                                        />
                                      </Form.Item>
                                      <Form.Item label="语气风格：">
                                        <Input
                                          value={promptFields.tone}
                                          placeholder="请输入语气风格"
                                          onChange={e => handlePromptFieldChange('tone', e.target.value)}
                                        />
                                      </Form.Item>
                                      <Form.Item label="视角：">
                                        <Input
                                          value={promptFields.persona}
                                          placeholder="请输入视角"
                                          onChange={e => handlePromptFieldChange('persona', e.target.value)}
                                        />
                                      </Form.Item>
                                      <Form.Item label="目标群体：">
                                        <Input
                                          value={promptFields.audience}
                                          placeholder="请输入目标群体"
                                          onChange={e => handlePromptFieldChange('audience', e.target.value)}
                                        />
                                      </Form.Item>
                                      <Form.Item label="内容长度：">
                                        <Input
                                          value={promptFields.word_count}
                                          placeholder="请输入内容长度"
                                          onChange={e => handlePromptFieldChange('word_count', e.target.value)}
                                        />
                                      </Form.Item>
                                      <Form.Item label="标题：">
                                        <Input
                                          value={promptFields.h2_count}
                                          placeholder="请输入标题"
                                          onChange={e => handlePromptFieldChange('h2_count', e.target.value)}
                                        />
                                      </Form.Item>
                                      <Form.Item label="选择关键词" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }} colon={false} style={{ marginBottom: 12 }}>
                                        <Select
                                          style={{ width: '100%' }}
                                          placeholder="请选择分析后的关键词"
                                          value={activeState.selectedKeyword}
                                          onChange={handleSelectKeyword}
                                          options={activeState.analyzedKeywords.map(item => ({ label: item.keyword, value: item.keyword }))}
                                          showSearch
                                          optionFilterProp="label"
                                          allowClear
                                        />
                                      </Form.Item>
                                    </Form>
                                    <div style={{ margin: '24px 0 0 0', padding: 12, borderRadius: 4, minHeight: 80 }}>
                                      <Spin spinning={activeState.loadingKeywords}>
                                        {activeState.selectedKeywordData && (
                                          <div style={{ marginTop: 16 }}>
                                            <div><b>关键词：</b>{activeState.selectedKeywordData.keyword}</div>
                                            <div style={{ marginTop: 8 }}><b>SERP结果：</b>
                                              <div>
                                                <b>相关结果：</b>
                                                <ul>
                                                  {activeState.selectedKeywordData.serp_data?.organic_results?.map((r, idx) => (
                                                    <li key={idx}><a href={r.link} target="_blank" rel="noopener noreferrer">{r.title}</a></li>
                                                  ))}
                                                </ul>
                                              </div>
                                              <div>
                                                <b>相关搜索：</b>
                                                <ul>
                                                  {activeState.selectedKeywordData.serp_data?.related_searches?.map((s, idx) => (
                                                    <li key={idx}>{s}</li>
                                                  ))}
                                                </ul>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </Spin>
                                    </div>
                                  </Col>
                                  <Col span={14}>
                                    <div style={{ fontWeight: 'bold', marginBottom: 12 }}>Prompt 预览区</div>
                                    <div style={{ background: '#ddd', minHeight: 320, padding: 18, whiteSpace: 'pre-wrap', borderRadius: 4 }}>
                                      {renderPromptPreview(activeState.selectedKeywordData)}
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                            </div>
                          ),
                        },
                        {
                          label: 'Blog上传',
                          key: '3',
                          children: (
                            <div>
                              <Space size={28}>
                                <Upload
                                  beforeUpload={() => false}
                                  accept=".xls,.xlsx"
                                  maxCount={1}
                                  showUploadList={false}
                                  onChange={handleInformationalExcelUpload}
                                >
                                  <Button type="primary" icon={<UploadOutlined />} loading={activeState.uploading}>
                                    Excel上传
                                  </Button>
                                </Upload>
                                <Button
                                  type="primary"
                                  icon={<DownloadOutlined />}
                                  disabled={!activeState.informationalCanDownloadMarkdown}
                                  onClick={() => {
                                    if (activeState.informationalCanDownloadMarkdown && activeState.informationalDownloadMarkdownUrl) {
                                      window.open(activeState.informationalDownloadMarkdownUrl);
                                    } else {
                                      message.warning('暂无可下载的Informational Markdown文件');
                                    }
                                  }}
                                >下载SERP结果MD</Button>
                                <Button
                                  type="primary"
                                  icon={<DownloadOutlined />}
                                  disabled={!activeState.informationalCanDownloadJson}
                                  onClick={() => {
                                    if (activeState.informationalCanDownloadJson && activeState.informationalDownloadJsonUrl) {
                                      window.open(activeState.informationalDownloadJsonUrl);
                                    } else {
                                      message.warning('暂无可下载的Informational JSON文件');
                                    }
                                  }}
                                >下载SERP结果JSON</Button>
                                <Button
                                  type="primary"
                                  icon={<PlusOutlined />}
                                  loading={activeState.informationalBlogGenerating}
                                  onClick={handleGenerateInformationalBlogs}
                                >Blog生成</Button>
                                <Button
                                  type="primary"
                                  icon={<DownloadOutlined />}
                                  disabled={!activeState.informationalBlogExcelUrl}
                                  onClick={() => {
                                    if (activeState.informationalBlogExcelUrl) {
                                      window.open(activeState.informationalBlogExcelUrl);
                                    } else {
                                      message.warning('暂无可下载的Informational Blog Excel文件');
                                    }
                                  }}
                                >
                                  Blog导出
                                </Button>
                                {/* Shopify blogs获取按钮 */}
                                <div style={{ marginBottom: 2 }}>
                                  <Button
                                    type="primary"
                                    icon={<DownloadOutlined />}
                                    loading={activeState.loadingShopifyBlogs}
                                    onClick={fetchShopifyBlogs}
                                  >
                                    获取Shopify Blogs
                                  </Button>
                                  <Button
                                    type="primary"
                                    style={{ marginLeft: 16 }}
                                    onClick={() => {
                                      setInformationalCustomPromptEditing(activeState.informationalCustomPrompt || '');
                                      setInformationalCustomPromptModalOpen(true);
                                    }}
                                  >
                                    Prompt自定义
                                  </Button>
                                </div>
                              </Space>

                              {/* 生成的Informational文章列表和上传按钮 */}
                              {activeState.generatedInformationalArticles.length > 0 && (
                                <div style={{ marginTop: 24, border: '1px solid #d9d9d9', borderRadius: 6, padding: 16 }}>
                                  {/* 列表标题 + 配置按钮 头部 */}
                                  <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 12
                                  }}>
                                    <div style={{ fontWeight: 'bold' }}>生成的Informational文章列表</div>

                                  </div>

                                  {/* 文章项列表 */}
                                  <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                                    {activeState.generatedInformationalArticles.map((article, index) => (
                                      <div key={index} style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '8px 0',
                                        borderBottom: index < activeState.generatedInformationalArticles.length - 1 ? '1px solid #f0f0f0' : 'none'
                                      }}>
                                        <span>{article.keyword}</span>
                                        <Space>
                                          <Button
                                            size="small"
                                            type="primary"
                                            onClick={() => handleUploadToWordPress(article.article_id)}
                                          >
                                            上传到WordPress
                                          </Button>
                                          <Select
                                            size="small"
                                            style={{ width: 200 }}
                                            placeholder="选择Shopify Blog"
                                            value={activeState.selectedShopifyBlogId}
                                            onChange={handleSelectShopifyBlog}
                                            options={activeState.shopifyBlogs.map(blog => ({
                                              label: blog.title,
                                              value: blog.id.toString()
                                            }))}
                                          />
                                          <Button
                                            size="small"
                                            type="primary"
                                            disabled={!activeState.selectedShopifyBlogId}
                                            onClick={() => handleUploadToShopify(article.article_id, activeState.selectedShopifyBlogId)}
                                          >
                                            上传到Shopify
                                          </Button>
                                        </Space>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              <div style={{ marginTop: 32, border: '1px solid #ccc', borderRadius: 6, padding: 24 }}>
                                <Row gutter={32}>
                                  <Col span={10}>
                                    <div style={{ fontWeight: 'bold', marginBottom: 12 }}>Prompt 修改区</div>
                                    <Form layout="vertical">
                                      <Form.Item label="品牌名称：">
                                        <Input
                                          value={promptFields.brand_name}
                                          placeholder="请输入品牌名称"
                                          onChange={e => handlePromptFieldChange('brand_name', e.target.value)}
                                        />
                                      </Form.Item>
                                      <Form.Item label="产品或部件：">
                                        <Input
                                          value={promptFields.product_or_parts}
                                          placeholder="请输入产品或部件"
                                          onChange={e => handlePromptFieldChange('product_or_parts', e.target.value)}
                                        />
                                      </Form.Item>
                                      <Form.Item label="目标市场：">
                                        <Input
                                          value={promptFields.target_market}
                                          placeholder="请输入目标市场"
                                          onChange={e => handlePromptFieldChange('target_market', e.target.value)}
                                        />
                                      </Form.Item>
                                      <Form.Item label="语言：">
                                        <Input
                                          value={promptFields.language}
                                          placeholder="请输入语言"
                                          onChange={e => handlePromptFieldChange('language', e.target.value)}
                                        />
                                      </Form.Item>
                                      <Form.Item label="公司优势：">
                                        <Input
                                          value={promptFields.company_advantages}
                                          placeholder="请输入公司优势"
                                          onChange={e => handlePromptFieldChange('company_advantages', e.target.value)}
                                        />
                                      </Form.Item>
                                      <Form.Item label="选择关键词" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }} colon={false} style={{ marginBottom: 12 }}>
                                        <Select
                                          style={{ width: '100%' }}
                                          placeholder="请选择分析后的关键词"
                                          value={activeState.informationalSelectedKeyword}
                                          onChange={handleSelectInformationalKeyword}
                                          options={activeState.informationalKeywords.map(item => ({ label: item.keyword, value: item.keyword }))}
                                          showSearch
                                          optionFilterProp="label"
                                          allowClear
                                        />
                                      </Form.Item>
                                    </Form>
                                    <div style={{ margin: '24px 0 0 0', padding: 12, borderRadius: 4, minHeight: 80 }}>
                                      <Spin spinning={activeState.informationalLoadingKeywords}>
                                        {activeState.informationalSelectedKeywordData && (
                                          <div style={{ marginTop: 16 }}>
                                            <div><b>关键词：</b>{activeState.informationalSelectedKeywordData.keyword}</div>
                                            <div style={{ marginTop: 8 }}><b>SERP结果：</b>
                                              <div>
                                                <b>相关结果：</b>
                                                <ul>
                                                  {activeState.informationalSelectedKeywordData.serp_data?.organic_results?.map((r, idx) => (
                                                    <li key={idx}><a href={r.link} target="_blank" rel="noopener noreferrer">{r.title}</a></li>
                                                  ))}
                                                </ul>
                                              </div>
                                              <div>
                                                <b>相关搜索：</b>
                                                <ul>
                                                  {activeState.informationalSelectedKeywordData.serp_data?.related_searches?.map((s, idx) => (
                                                    <li key={idx}>{s}</li>
                                                  ))}
                                                </ul>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </Spin>
                                    </div>
                                  </Col>
                                  <Col span={14}>
                                    <div style={{ fontWeight: 'bold', marginBottom: 12 }}>Prompt 预览区</div>
                                    <div style={{ background: '#ddd', minHeight: 320, padding: 18, whiteSpace: 'pre-wrap', borderRadius: 4 }}>
                                      {renderInformationalPromptPreview(activeState.informationalSelectedKeywordData)}
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                            </div>
                          ),
                        },
                      ]}
                    />
                  </>
                )}
              </Layout.Content>

              <Modal
                title="自定义Prompt"
                open={customPromptModalOpen}
                onOk={() => {
                  console.log('🔄 Saving custom prompt for project:', activeProjectName, 'Content:', customPromptEditing.substring(0, 100) + '...');
                  updateProjectState(activeProjectName, { customPrompt: customPromptEditing });
                  setCustomPromptModalOpen(false);
                  addLog('success', '已保存自定义Prompt，Blog生成将优先使用该内容');
                }}
                onCancel={() => setCustomPromptModalOpen(false)}
                okText="保存"
                cancelText="取消"
              >
                <Input.TextArea
                  rows={14}
                  value={customPromptEditing}
                  onChange={e => setCustomPromptEditing(e.target.value)}
                  placeholder="请输入自定义Prompt。保存后会优先用此内容作为AI写作提示。"
                  style={{ fontFamily: 'monospace' }}
                />
              </Modal>

              <Modal
                title="自定义Informational Prompt"
                open={informationalCustomPromptModalOpen}
                onOk={() => {
                  console.log('🔄 Saving informational custom prompt for project:', activeProjectName, 'Content:', informationalCustomPromptEditing.substring(0, 100) + '...');
                  updateProjectState(activeProjectName, { informationalCustomPrompt: informationalCustomPromptEditing });
                  setInformationalCustomPromptModalOpen(false);
                  addLog('success', '已保存Informational自定义Prompt，Informational Blog生成将优先使用该内容');
                }}
                onCancel={() => setInformationalCustomPromptModalOpen(false)}
                okText="保存"
                cancelText="取消"
              >
                <Input.TextArea
                  rows={14}
                  value={informationalCustomPromptEditing}
                  onChange={e => setInformationalCustomPromptEditing(e.target.value)}
                  placeholder="请输入自定义Prompt。保存后会优先用此内容作为Informational Blog写作提示。"
                  style={{ fontFamily: 'monospace' }}
                />
              </Modal>

              <Modal
                title="切换大模型"
                open={modelSelectModalOpen}
                onOk={() => {
                  setGlobalModel(modelSelectTemp);
                  setModelSelectModalOpen(false);
                  message.success(`已切换至 ${modelList.find(m => m.value === modelSelectTemp)?.label || modelSelectTemp}`);
                }}
                onCancel={() => setModelSelectModalOpen(false)}
                okText="确定"
                cancelText="取消"
              >
                <div style={{ padding: '8px 0' }}>
                  <Radio.Group
                    value={modelSelectTemp}
                    onChange={e => setModelSelectTemp(e.target.value)}
                    style={{ width: '100%' }}
                  >
                    <div style={{ fontWeight: 'bold', color: '#1677ff', marginBottom: 4 }}>ChatGPT系列</div>
                    {modelList.filter(m => m.group === 'ChatGPT').map(m => (
                      <Radio key={m.value} value={m.value} style={{ display: 'block', marginLeft: 16, marginBottom: 4 }}>
                        {m.label}
                      </Radio>
                    ))}
                    <div style={{ fontWeight: 'bold', color: '#0a5496', margin: '16px 0 4px 0' }}>Claude系列</div>
                    {modelList.filter(m => m.group === 'Claude').map(m => (
                      <Radio key={m.value} value={m.value} style={{ display: 'block', marginLeft: 16, marginBottom: 4 }}>
                        {m.label}
                      </Radio>
                    ))}
                  </Radio.Group>

                </div>
              </Modal>

              <Modal
                title="配置 WordPress 账户"
                open={wpConfigModalOpen}
                onCancel={() => setWpConfigModalOpen(false)}
                footer={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {/* 左侧测试连接 */}
                    <div>
                      <Button
                        type="default"
                        loading={wpTesting}
                        onClick={handleTestWpConnection}
                        style={{ marginRight: 8 }}
                      >
                        测试连接
                      </Button>
                      {wpTestStatus === 'success' && (
                        <span style={{ color: 'green', fontWeight: 600 }}>✔ 已连接</span>
                      )}
                      {wpTestStatus === 'fail' && (
                        <span style={{ color: 'red', fontWeight: 600 }}>✖ 连接失败</span>
                      )}
                    </div>

                    {/* 右侧取消/保存 */}
                    <div>
                      <Button onClick={() => setWpConfigModalOpen(false)} style={{ marginRight: 8 }}>
                        取消
                      </Button>
                      <Button
                        type="primary"
                        onClick={() => {
                          setProjectWpConfig(prev => ({
                            ...prev,
                            [activeProjectName]: { ...wpConfigEditing }
                          }));
                          setWpConfigModalOpen(false);
                          message.success('WordPress 配置已保存到本项目');
                        }}
                      >
                        保存
                      </Button>
                    </div>
                  </div>
                }
              >
                <Form layout="vertical">
                  <Form.Item label="WordPress 站点地址">
                    <Input
                      placeholder="如 https://yourwp.com"
                      value={wpConfigEditing.wp_url}
                      onChange={e =>
                        setWpConfigEditing(prev => ({
                          ...prev,
                          wp_url: e.target.value
                        }))
                      }
                    />
                  </Form.Item>
                  <Form.Item label="用户名">
                    <Input
                      placeholder="如 admin"
                      value={wpConfigEditing.wp_username}
                      onChange={e =>
                        setWpConfigEditing(prev => ({
                          ...prev,
                          wp_username: e.target.value
                        }))
                      }
                    />
                  </Form.Item>
                  <Form.Item label="应用密码">
                    <Input.Password
                      placeholder="WordPress 应用密码"
                      value={wpConfigEditing.wp_app_password}
                      onChange={e =>
                        setWpConfigEditing(prev => ({
                          ...prev,
                          wp_app_password: e.target.value
                        }))
                      }
                    />
                  </Form.Item>
                </Form>
              </Modal>


              <Modal
                title="配置 Shopify"
                open={shopifyConfigModalOpen}
                onCancel={() => setShopifyConfigModalOpen(false)}
                footer={
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    {/* 左侧：测试连接 + 状态 */}
                    <div>
                      <Button
                        type="default"
                        loading={shopifyTesting}
                        onClick={handleTestShopifyConnection}
                        style={{ marginRight: 8 }}
                      >
                        测试连接
                      </Button>
                      {shopifyTestStatus === 'success' && (
                        <span style={{ color: 'green', fontWeight: 600 }}>✔ 已连接</span>
                      )}
                      {shopifyTestStatus === 'fail' && (
                        <span style={{ color: 'red', fontWeight: 600 }}>✖ 连接失败</span>
                      )}
                    </div>

                    {/* 右侧：取消 + 保存 */}
                    <div>
                      <Button onClick={() => setShopifyConfigModalOpen(false)} style={{ marginRight: 8 }}>
                        取消
                      </Button>
                      <Button
                        type="primary"
                        onClick={() => {
                          setProjectShopifyConfig(prev => ({
                            ...prev,
                            [activeProjectName]: { ...shopifyConfigEditing }
                          }));
                          setShopifyConfigModalOpen(false);
                          message.success('Shopify 配置已保存到本项目');
                        }}
                      >
                        保存
                      </Button>
                    </div>
                  </div>
                }
              >
                {/* 表单内容 */}
                <Form layout="vertical">
                  <Form.Item label="Shopify 店铺域名">
                    <Input
                      placeholder="如 myshop.myshopify.com"
                      value={shopifyConfigEditing.shop_domain}
                      onChange={e =>
                        setShopifyConfigEditing(prev => ({
                          ...prev,
                          shop_domain: e.target.value
                        }))
                      }
                    />
                  </Form.Item>
                  <Form.Item label="API Access Token">
                    <Input.Password
                      placeholder="Shopify Private App Access Token"
                      value={shopifyConfigEditing.access_token}
                      onChange={e =>
                        setShopifyConfigEditing(prev => ({
                          ...prev,
                          access_token: e.target.value
                        }))
                      }
                    />
                  </Form.Item>
                </Form>
              </Modal>

              {/* 底部日志区域（项目隔离） */}
              <Layout.Footer
                style={{
                  flex: '0 0 140px',
                  background: '#fafafa',
                  padding: '12px 32px',
                  borderTop: '1px solid #e8e8e8',
                  overflowY: 'auto'
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>系统日志</div>
                {logs.map((log, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                    {log.type === 'success' && <SmileOutlined style={{ color: 'green', marginRight: 8 }} />}
                    {log.type === 'error' && <FrownOutlined style={{ color: 'red', marginRight: 8 }} />}
                    {log.type === 'warning' && <MehOutlined style={{ color: 'orange', marginRight: 8 }} />}
                    <span>[{log.timestamp}] {log.message}</span>
                  </div>
                ))}
              </Layout.Footer>
            </Layout>
          </Layout>
        </Layout>
      </SignedIn>
    </>
  );
}

export default App;
